// qs-util-layerLock.ts
import { useLayerStore } from '@/stores/qs-store-layers'

export class LayerLockValidator {
  private static get layerStore() {
    return useLayerStore()
  }

  static isLayerLocked(layerId: string | null): boolean {
    if (!layerId) return false
    
    const layer = this.layerStore.layers.find(l => l.id === layerId)
    return layer?.locked || false
  }

  static isCurrentLayerLocked(): boolean {
    return this.isLayerLocked(this.layerStore.currentLayerId)
  }

  static validateLayerOperation(layerId: string | null, operation: string): {
    canProceed: boolean
    errorMessage?: string
  } {
    if (!layerId) {
      return {
        canProceed: false,
        errorMessage: 'No layer selected'
      }
    }

    if (this.isLayerLocked(layerId)) {
      const layer = this.layerStore.layers.find(l => l.id === layerId)
      return {
        canProceed: false,
        errorMessage: `Cannot ${operation} - layer "${layer?.name || layerId}" is locked`
      }
    }

    return { canProceed: true }
  }

  static validateCurrentLayerOperation(operation: string): {
    canProceed: boolean
    errorMessage?: string
  } {
    return this.validateLayerOperation(this.layerStore.currentLayerId, operation)
  }

  static getUnlockedLayers(): string[] {
    return this.layerStore.layers
      .filter(layer => !layer.locked)
      .map(layer => layer.id)
  }

  static getFirstUnlockedLayer(): string | null {
    const unlockedLayers = this.getUnlockedLayers()
    return unlockedLayers.length > 0 ? unlockedLayers[0] : null
  }

  static hasUnlockedLayers(): boolean {
    return this.getUnlockedLayers().length > 0
  }
}

export const isLayerLocked = (layerId: string | null) => LayerLockValidator.isLayerLocked(layerId)
export const isCurrentLayerLocked = () => LayerLockValidator.isCurrentLayerLocked()
export const validateLayerOperation = (layerId: string | null, operation: string) => LayerLockValidator.validateLayerOperation(layerId, operation)
export const validateCurrentLayerOperation = (operation: string) => LayerLockValidator.validateCurrentLayerOperation(operation)
export const getUnlockedLayers = () => LayerLockValidator.getUnlockedLayers()
export const getFirstUnlockedLayer = () => LayerLockValidator.getFirstUnlockedLayer()
export const hasUnlockedLayers = () => LayerLockValidator.hasUnlockedLayers()